import dayjs from "dayjs";
import { useMemo, useState } from "react";
import type { DateFilterType } from "./DateRangeFilter";

interface UseDateRangeFilterResult {
  selectedDate: dayjs.Dayjs;
  dateFilterType: DateFilterType;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  handleDateChange: (date: dayjs.Dayjs) => void;
  handleFilterTypeChange: (type: DateFilterType) => void;
}

interface UseDateRangeFilterProps {
  defaultFilterType?: DateFilterType;
}

/**
 * Hook para gerenciar filtros de data
 * Calcula automaticamente o intervalo de datas baseado no tipo de filtro
 *
 * @param props.defaultFilterType - Tipo de filtro inicial ('day' | 'week' | 'month' | 'year'). Default: 'day'
 *
 * @example
 * ```tsx
 * // Usando com tipo default
 * const { dateRange } = useDateRangeFilter({ defaultFilterType: 'month' });
 *
 * // Usando com tipo padrão (day)
 * const { dateRange } = useDateRangeFilter();
 * ```
 */
export const useDateRangeFilter = (
  props?: UseDateRangeFilterProps
): UseDateRangeFilterResult => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>(
    props?.defaultFilterType || "day"
  );

  // Calcula o intervalo de datas baseado no tipo de filtro
  const dateRange = useMemo(() => {
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs;

    switch (dateFilterType) {
      case "week":
        startDate = selectedDate.startOf("week");
        endDate = selectedDate.endOf("week");
        break;
      case "month":
        startDate = selectedDate.startOf("month");
        endDate = selectedDate.endOf("month");
        break;
      case "year":
        startDate = selectedDate.startOf("year");
        endDate = selectedDate.endOf("year");
        break;
      case "day":
      default:
        startDate = selectedDate.startOf("day");
        endDate = selectedDate.endOf("day");
    }

    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  }, [selectedDate, dateFilterType]);

  const handleDateChange = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
  };

  const handleFilterTypeChange = (type: DateFilterType) => {
    setDateFilterType(type);
  };

  return {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  };
};
